import { ContentObserver } from '@angular/cdk/observers';
import { Component, DestroyRef, ElementRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'portal-modal-body',
  templateUrl: './modal-body.component.html',
  styleUrls: ['./modal-body.component.scss'],
  host: {
    class: 'd-block modal-body',
  },
  standalone: false,
})
export class ModalBodyComponent implements OnInit {
  private get modalBody(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  constructor(
    private elementRef: ElementRef,
    private contentObserver: ContentObserver,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    // Update event listeners whenever content changes
    this.contentObserver
      .observe(this.elementRef)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.modalBody?.querySelectorAll('label[for]').forEach((label: HTMLLabelElement) => {
          // Remove previously added event listeners, so we don't end up with more than one per element
          label.removeEventListener('click', this.labelClick);
          label.addEventListener('click', this.labelClick);
        });
      });
  }

  private labelClick = (event: MouseEvent): void => {
    if (event.currentTarget instanceof HTMLLabelElement) {
      const targetIsCheckbox = event.target instanceof HTMLInputElement && event.target.type === 'checkbox';
      const targetIsAnotherLabel =
        event.target instanceof HTMLLabelElement && event.target !== event.currentTarget;

      if (targetIsCheckbox || targetIsAnotherLabel) {
        // In some cases we use a checkbox with its own label
        // inside the actual label of a form control. This
        // should not trigger the label that contains it.
        event.stopPropagation();
        return;
      }

      const labelTarget = event.currentTarget.htmlFor;
      const selector = `#${CSS.escape(labelTarget)}`;
      const target = this.modalBody.querySelector(selector);

      if (target == null) {
        return;
      }

      if (target.classList.contains('ng-select')) {
        // Fix ng-select not focusing when clicking on a label
        const searchElement = target.querySelector('.ng-input input') as HTMLInputElement | undefined;

        if (searchElement != null) {
          searchElement.focus();
        }
      } else if (target instanceof HTMLInputElement) {
        event.preventDefault();
        this.focusInput(target);
      } else if ('focus' in target && typeof target.focus === 'function') {
        event.preventDefault();
        target.focus();
      }
    }
  };

  private focusInput(input: HTMLInputElement): void {
    // Checkboxes and radio buttons also need to trigger changes, so we do not only focus them, but also simulate the user clicking on them.
    if (input.type === 'checkbox' || input.type === 'radio') {
      input.focus();
      input.click();
    } else {
      input.focus();
    }
  }
}
